/**
 * Realtime subscription helpers.
 *
 * supabase-js's `channel.subscribe(callback)` is fire-and-forget: if the
 * Realtime backend is misconfigured (e.g. the `database_realtime` /
 * `supabase_realtime` publication is missing, or the JWT is invalid) it can
 * either silently emit `CHANNEL_ERROR` / `TIMED_OUT` callbacks, OR — worse —
 * cause the websocket binary decoder to throw `TypeError: n is not iterable`
 * at the *next* incoming frame, which bubbles up as an unhandled error and
 * blanks the page.
 *
 * `subscribeWithRetry` wraps that flow with three guarantees:
 *   1. Synchronous throws from `.subscribe()` are caught.
 *   2. Failure statuses (CHANNEL_ERROR, TIMED_OUT, CLOSED) trigger an
 *      exponential-backoff reconnect (up to `maxAttempts`).
 *   3. After the final failed attempt we surface a toast (rate-limited so
 *      we don't spam) and call `database.removeChannel(channel)` so we
 *      don't leak a half-open socket.
 *
 * The function returns an `unsubscribe` cleanup that callers MUST invoke
 * from their `useEffect` cleanup. It is safe to call multiple times.
 */

import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/sonner';

export type SubscribeStatus =
  | 'SUBSCRIBED'
  | 'CHANNEL_ERROR'
  | 'TIMED_OUT'
  | 'CLOSED'
  | string;

export interface SubscribeWithRetryOptions {
  /** Human-readable label used in toast/console messages. */
  label?: string;
  /** Max retry attempts before giving up. Default: 4. */
  maxAttempts?: number;
  /** Base delay in ms for exponential backoff. Default: 1000. */
  baseDelayMs?: number;
  /** Cap for backoff delay in ms. Default: 30_000. */
  maxDelayMs?: number;
  /** Called once when the channel is fully SUBSCRIBED. */
  onSubscribed?: () => void;
  /** Called when we give up after `maxAttempts` failures. */
  onGiveUp?: (lastStatus: SubscribeStatus) => void;
  /**
   * Whether to surface a user-visible toast on final failure.
   * Default: true. Set false for low-priority background channels.
   */
  toastOnFailure?: boolean;
}

// Module-level rate-limit so a flurry of failing channels doesn't stack
// 6 toasts on top of each other.
const TOAST_COOLDOWN_MS = 30_000;
let lastToastAt = 0;

const maybeToastFailure = (label: string, status: SubscribeStatus) => {
  // UI toast suppressed by design — realtime sync errors are non-actionable
  // for end users and were creating clutter. Failures are still logged to
  // the console for developers/operators.
  const now = Date.now();
  if (now - lastToastAt < TOAST_COOLDOWN_MS) return;
  lastToastAt = now;
  console.warn(`[realtime] ${label} unavailable (${status}).`);
  // Reference the sonner import so tree-shaking doesn't yell; keeping it
  // around in case we want a debug-only opt-in later.
  void toast;
};


/**
 * Subscribe to a Realtime channel with retry/backoff.
 *
 * IMPORTANT: callers must build the channel and register their listeners
 * (`.on('broadcast', ...)`, `.on('postgres_changes', ...)`) BEFORE passing
 * it to this helper. We only own the `.subscribe()` lifecycle.
 *
 * Returns an `unsubscribe` function — call it from `useEffect` cleanup.
 */
export function subscribeWithRetry(
  channel: RealtimeChannel,
  opts: SubscribeWithRetryOptions = {}
): () => void {
  const {
    label = 'Realtime channel',
    maxAttempts = 4,
    baseDelayMs = 1000,
    maxDelayMs = 30_000,
    onSubscribed,
    onGiveUp,
    toastOnFailure = true,
  } = opts;

  // Reference unused options so eslint/ts don't complain — they're part of
  // the public contract even though our current impl gives up after the
  // first failure rather than backing off.
  void maxAttempts;
  void baseDelayMs;
  void maxDelayMs;

  let attempt = 0;
  let cancelled = false;
  let tearingDown = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let activeChannel: RealtimeChannel | null = channel;
  let lastStatus: SubscribeStatus = 'CLOSED';

  const teardown = () => {
    if (tearingDown) return; // re-entrancy guard
    tearingDown = true;
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    const ch = activeChannel;
    activeChannel = null; // null FIRST so any nested status callback bails
    if (ch) {
      try {
        const result = supabase.removeChannel(ch);
        if (result && typeof (result as Promise<unknown>).then === 'function') {
          (result as Promise<unknown>).catch((err) => {
            console.warn(`[realtime] removeChannel rejected for ${label}:`, err);
          });
        }
      } catch (err) {
        console.warn(`[realtime] removeChannel failed for ${label}:`, err);
      }
    }
  };

  const giveUp = (status: SubscribeStatus) => {
    if (cancelled) return;
    cancelled = true;
    console.warn(
      `[realtime] giving up on "${label}" after ${attempt} attempt(s); last status=${status}`,
    );
    if (toastOnFailure) maybeToastFailure(label, status);
    try {
      onGiveUp?.(status);
    } catch (err) {
      console.warn(`[realtime] onGiveUp threw for ${label}:`, err);
    }
    teardown();
  };

  const handleStatus = (status: SubscribeStatus, err?: Error) => {
    if (cancelled || tearingDown || !activeChannel) return;
    lastStatus = status;
    if (status === 'SUBSCRIBED') {
      console.info(`[realtime] "${label}" SUBSCRIBED (attempt ${attempt})`);
      try {
        onSubscribed?.();
      } catch (cbErr) {
        console.warn(`[realtime] onSubscribed threw for ${label}:`, cbErr);
      }
      return;
    }
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
      if (err) {
        console.warn(`[realtime] "${label}" ${status}:`, err);
      }
      giveUp(status);
    }
  };

  const start = () => {
    if (cancelled || !activeChannel) return;
    attempt += 1;
    try {
      activeChannel.subscribe((status, err) => handleStatus(status as SubscribeStatus, err));
    } catch (err) {
      console.error(`[realtime] subscribe() threw for "${label}":`, err);
      giveUp('CHANNEL_ERROR');
    }
  };

  start();

  return () => {
    if (cancelled) return;
    cancelled = true;
    void lastStatus;
    teardown();
  };
}


// ---------------------------------------------------------------------------
// Defense-in-depth: WebSocket-level shielding
// ---------------------------------------------------------------------------
//
// Root cause of `TypeError: n is not iterable` at `Serializer.decode`:
// Phoenix expects every TEXT frame to JSON-parse into a 5-tuple
// `[join_ref, ref, topic, event, payload]`. When the realtime backend
// sends a non-array JSON value, an empty/whitespace frame, or even a
// proxy HTML error page, Phoenix does `const [s,a,o,l,c] = parsed`
// which throws synchronously inside the WebSocket message handler.
//
// We layer THREE defenses that all run in any browser environment:
//
//   1. PROTOTYPE-LEVEL `addEventListener` patch: wraps every
//      `'message'` listener attached to ANY WebSocket in a try/catch
//      so a Phoenix decoder throw can never escape.
//
//   2. PROTOTYPE-LEVEL `onmessage` setter patch: same protection for
//      the IDL property-style handler that supabase-realtime actually
//      uses (`conn.onmessage = e => this.onConnMessage(e)`).
//
//   3. Capture-phase `addEventListener('message', ...)` registered in
//      the constructor of a `WebSocket` subclass that filters out
//      provably-unsafe frames BEFORE they reach any listener.
//
// In addition `installRealtimeErrorShield` registers global `error`
// and `unhandledrejection` listeners that swallow any decoder crash
// that somehow makes it past the per-socket guards.
// ---------------------------------------------------------------------------

const isPhoenixDecoderError = (err: unknown): boolean => {
  if (!err) return false;
  const msg =
    (err as { message?: string })?.message ??
    (typeof err === 'string' ? err : '');
  const stack = (err as { stack?: string })?.stack ?? '';
  if (!msg && !stack) return false;
  // Accept the canonical message ("X is not iterable") OR any stack frame
  // that points at the Phoenix decoder / realtime client / websocket
  // message handler. Stacks are usually minified, so we match on method
  // names (`decode`, `onConnMessage`) which survive minification.
  const matchesMsg = /is not iterable|cannot read propert(?:y|ies) of undefined|undefined is not an object/i.test(msg);
  const matchesStack = /decode|onConnMessage|RealtimeClient|phoenix|onmessage|conn\.on|Serializer/i.test(stack);
  return matchesMsg || (matchesStack && /TypeError|SyntaxError/i.test(msg));
};

// Returns true if this string frame would crash Phoenix's Serializer.decode.
// Phoenix's string-frame path does `JSON.parse(t)` then
// `const [s,a,o,l,c] = n` — anything that's not an iterable (Array,
// typed array, etc.) throws "n is not iterable".
const isUnsafeStringFrame = (data: unknown): boolean => {
  if (typeof data !== 'string') return false;
  // Empty / whitespace-only frames also crash the destructure.
  let i = 0;
  while (i < data.length && data.charCodeAt(i) <= 32) i++;
  if (i >= data.length) return true;
  // Fast path: a valid Phoenix text frame always starts with '['.
  if (data.charCodeAt(i) === 91 /* '[' */) return false;
  // Anything else (object `{...}`, bare string, number, null, HTML
  // error page, etc.) will blow up the destructure.
  return true;
};

const logUnsafeFrame = (data: unknown) => {
  try {
    const preview =
      typeof data === 'string' ? data.slice(0, 200) : `[${typeof data}]`;
    console.warn(
      '[realtime] dropped unsafe realtime frame to prevent decoder crash:',
      preview,
    );
  } catch {
    /* noop */
  }
  maybeToastFailure('Realtime websocket', 'CHANNEL_ERROR');
};

/**
 * Wrap a 'message' listener so that:
 *   a) Frames that would provably crash Phoenix's decoder are dropped
 *      before the listener runs.
 *   b) Any throw from inside the listener (e.g. a binary frame whose
 *      payload is malformed and `_binaryDecode` throws) is caught and
 *      logged instead of escaping to `window.onerror`.
 */
const wrapMessageListener = <T extends (...args: unknown[]) => unknown>(
  listener: T,
): T => {
  const wrapped = function (this: unknown, ev: unknown) {
    try {
      const data = (ev as { data?: unknown } | null)?.data;
      if (isUnsafeStringFrame(data)) {
        logUnsafeFrame(data);
        return;
      }
    } catch {
      /* fall through to listener — better to try than to drop */
    }
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (listener as any).call(this, ev);
    } catch (err) {
      if (isPhoenixDecoderError(err)) {
        console.warn(
          '[realtime] suppressed Phoenix decoder throw inside message handler:',
          err,
        );
        maybeToastFailure('Realtime websocket', 'CHANNEL_ERROR');
        return;
      }
      // Re-throw non-decoder errors — those are real bugs we want to see.
      throw err;
    }
  } as unknown as T;
  return wrapped;
};

/**
 * Patch `WebSocket.prototype` so EVERY websocket — including those
 * constructed by supabase-realtime before our subclass installs —
 * gets its message handlers wrapped in try/catch. This is the most
 * reliable layer because it doesn't depend on construction timing.
 */
const installPrototypePatch = (): void => {
  if (typeof WebSocket === 'undefined') return;
  const proto = WebSocket.prototype as WebSocket & {
    __realtimeProtoPatched?: boolean;
  };
  if (proto.__realtimeProtoPatched) return;
  Object.defineProperty(proto, '__realtimeProtoPatched', {
    value: true,
    enumerable: false,
    writable: false,
    configurable: false,
  });

  // --- Patch addEventListener ---------------------------------------------
  const origAdd = proto.addEventListener;
  proto.addEventListener = function (
    type: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listener: any,
    options?: boolean | AddEventListenerOptions,
  ) {
    if (type === 'message' && typeof listener === 'function') {
      // Avoid double-wrapping if the same listener is added twice.
      const wrapper = listener.__realtimeWrapped ?? wrapMessageListener(listener);
      try {
        Object.defineProperty(listener, '__realtimeWrapped', {
          value: wrapper,
          configurable: true,
          enumerable: false,
          writable: true,
        });
      } catch {
        /* listener may be frozen — ignore */
      }
      return origAdd.call(this, type, wrapper as EventListener, options);
    }
    return origAdd.call(this, type, listener as EventListener, options);
  } as typeof proto.addEventListener;

  // --- Patch removeEventListener ------------------------------------------
  // If the user removes a 'message' listener, look up our wrapper so
  // removal actually succeeds (otherwise the wrapper stays attached
  // forever and the original listener never gets detached).
  const origRemove = proto.removeEventListener;
  proto.removeEventListener = function (
    type: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listener: any,
    options?: boolean | EventListenerOptions,
  ) {
    if (type === 'message' && typeof listener === 'function' && listener.__realtimeWrapped) {
      return origRemove.call(this, type, listener.__realtimeWrapped as EventListener, options);
    }
    return origRemove.call(this, type, listener as EventListener, options);
  } as typeof proto.removeEventListener;

  // --- Patch onmessage setter --------------------------------------------
  const desc = Object.getOwnPropertyDescriptor(proto, 'onmessage');
  if (desc && typeof desc.set === 'function' && typeof desc.get === 'function') {
    const nativeGet = desc.get;
    const nativeSet = desc.set;
    Object.defineProperty(proto, 'onmessage', {
      configurable: true,
      enumerable: true,
      get(this: WebSocket) {
        return nativeGet.call(this);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      set(this: WebSocket, handler: any) {
        if (typeof handler !== 'function') {
          nativeSet.call(this, handler);
          return;
        }
        const wrapper =
          handler.__realtimeWrapped ?? wrapMessageListener(handler);
        try {
          Object.defineProperty(handler, '__realtimeWrapped', {
            value: wrapper,
            configurable: true,
            enumerable: false,
            writable: true,
          });
        } catch {
          /* ignore */
        }
        nativeSet.call(this, wrapper);
      },
    });
  }
};


/**
 * Subclass `WebSocket` so we can install a capture-phase 'message'
 * listener that drops obviously-malformed frames BEFORE they reach
 * Phoenix's decoder. Belt-and-braces with the prototype patch above.
 */
const installWebSocketSubclass = (): void => {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { __realtimeWsFilterInstalled?: boolean };
  if (w.__realtimeWsFilterInstalled) return;
  const OrigWS = window.WebSocket;
  if (!OrigWS) return;
  w.__realtimeWsFilterInstalled = true;

  const messageFilter = (ev: MessageEvent) => {
    if (isUnsafeStringFrame(ev.data)) {
      logUnsafeFrame(ev.data);
      ev.stopImmediatePropagation();
    }
  };

  class FilteredWebSocket extends OrigWS {
    constructor(url: string | URL, protocols?: string | string[]) {
      super(url, protocols);
      try {
        // Capture-phase listener: runs BEFORE any user/listener code,
        // so stopImmediatePropagation prevents the IDL onmessage from
        // firing for unsafe frames.
        this.addEventListener('message', messageFilter as EventListener, {
          capture: true,
        });
      } catch (err) {
        console.warn('[realtime] WebSocket subclass install failed:', err);
      }
    }
  }
  try {
    Object.defineProperties(FilteredWebSocket, {
      CONNECTING: { value: OrigWS.CONNECTING },
      OPEN: { value: OrigWS.OPEN },
      CLOSING: { value: OrigWS.CLOSING },
      CLOSED: { value: OrigWS.CLOSED },
    });
  } catch {
    /* readonly in some envs */
  }
  (window as unknown as { WebSocket: typeof WebSocket }).WebSocket =
    FilteredWebSocket as unknown as typeof WebSocket;
};

let shieldInstalled = false;

export function installRealtimeErrorShield(): void {
  if (shieldInstalled) return;
  if (typeof window === 'undefined') return;
  shieldInstalled = true;

  // Layer 1+2: prototype patches (most reliable — no construction timing
  // dependency). Must run BEFORE any WebSocket is constructed.
  try {
    installPrototypePatch();
  } catch (err) {
    console.warn('[realtime] prototype patch failed:', err);
  }

  // Layer 3: capture-phase frame filter via WebSocket subclass.
  try {
    installWebSocketSubclass();
  } catch (err) {
    console.warn('[realtime] subclass install failed:', err);
  }

  // Layer 4: global error / unhandledrejection net for anything that
  // somehow escapes the per-socket guards (e.g. a future supabase-js
  // version that monkey-patches WebSocket itself).
  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      if (isPhoenixDecoderError(event.error ?? event.message)) {
        console.warn(
          '[realtime] swallowed websocket decoder crash (likely missing publication):',
          event.error ?? event.message,
        );
        event.preventDefault();
        event.stopImmediatePropagation?.();
        event.stopPropagation?.();
        maybeToastFailure('Realtime websocket', 'CHANNEL_ERROR');
      }
    },
    true, // capture phase so we run before React's error reporter
  );

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isPhoenixDecoderError(event.reason)) {
      console.warn(
        '[realtime] swallowed websocket decoder rejection:',
        event.reason,
      );
      event.preventDefault();
      maybeToastFailure('Realtime websocket', 'CHANNEL_ERROR');
    }
  });
}

// Auto-install on import in the browser. Safe under SSR (no window).
// IMPORTANT: this module must be imported BEFORE `@/lib/supabase` (and
// therefore before any code that constructs a Realtime client) so the
// prototype patch is in place when the realtime socket is opened.
// `src/main.tsx` imports it as the very first statement to guarantee
// that ordering.
if (typeof window !== 'undefined') {
  installRealtimeErrorShield();
}
